import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { withTheme } from 'common/styling/theme';
import { BasicModal, DefaultButton } from 'common/components';

import { createBackOfficePaymentExport } from 'backoffice/payments/_redux';

import UserPayments from '../UserPayments';
import UserTotalPayments from '../UserTotalPayments';
import UserInternalTransfers from '../UserInternalTransfers';
import UserWalletPaymentForm from '../UserWalletPaymentForm';
import UserTotalPSPs from '../UserTotalPSPs';

import { staticStyles, getDynamicStyles } from './style';

const UserOperations = ({
  profile,
  user,
  payments,
  addons,
  theme,
  createBackOfficePaymentExport,
  internalTransfers,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [activeTab, handleChangeTab] = useState('dw');
  const history = useHistory();

  const exportsMap = {
    dw: { exportsType: 'payment_export', page: 'payment' },
    totalPayments: { exportsType: 'payment_export', page: 'payment' },
    transfers: { exportsType: 'internal_transfer_export', page: 'transfers' },
  };

  const handleCreateUserPaymentExport = () => {
    createBackOfficePaymentExport({
      ext: 'xlsx',
      filters: { user_id: user.id },
      exportsType: exportsMap[activeTab].exportsType,
      pushToExportsFunc: () => history.push(`/backoffice/${exportsMap[activeTab].page}/exports`),
    });
  };

  const hasRulesCorrectWallet =
    (profile.isAdmin ||
      profile.perms.walletBalanceCorrectionPerm ||
      profile.perms.managerWalletBalanceCorrectionPerm ||
      profile.perms.superUserPerm) &&
    (addons.includes('wallet') || addons.includes('walletAccounts'));

  const handleModalToggle = () => {
    setIsModalOpen(isOpen => !isOpen);
  };

  return (
    <section className="UserOperations">
      <div className="UserOperations__tabs">
        <FormattedMessage id="depositsAndWithdrawals">
          {txt => (
            <button
              type="button"
              className={classNames('UserOperations__tab', {
                active: activeTab === 'dw',
              })}
              onClick={() => handleChangeTab('dw')}>
              {txt}
            </button>
          )}
        </FormattedMessage>
        {(internalTransfers?.accounts_enabled ||
          internalTransfers?.traders_enabled ||
          internalTransfers?.from_wallet_enabled ||
          internalTransfers?.to_wallet_enabled ||
          internalTransfers?.from_wallet_account_enabled ||
          internalTransfers?.to_wallet_account_enabled) && (
          <FormattedMessage id="boInternalTransfers">
            {txt => (
              <button
                type="button"
                className={classNames('UserOperations__tab', {
                  active: activeTab === 'transfers',
                })}
                onClick={() => handleChangeTab('transfers')}>
                {txt}
              </button>
            )}
          </FormattedMessage>
        )}
        <FormattedMessage id="totalPayments">
          {txt => (
            <button
              type="button"
              className={classNames('UserOperations__tab', {
                active: activeTab === 'totalPayments',
              })}
              onClick={() => handleChangeTab('totalPayments')}>
              {txt}
            </button>
          )}
        </FormattedMessage>
        <FormattedMessage id="justPSPs">
          {txt => (
            <button
              type="button"
              className={classNames('UserOperations__tab', {
                active: activeTab === 'PSPs',
              })}
              onClick={() => handleChangeTab('PSPs')}>
              {txt}
            </button>
          )}
        </FormattedMessage>
      </div>
      <div className="UserOperations__content">
        {addons.includes('wallet') && (
          <div className="UserOperations__wallet-balance">
            <FormattedMessage id="justWalletBalance">
              {txt => <span className="UserOperations__wallet-balance-caption">{`${txt}:`}</span>}
            </FormattedMessage>
            {`$${user.walletBalance}`}
          </div>
        )}

        {(hasRulesCorrectWallet || payments.count > 0) && (
          <div className="UserOperations__content_actions">
            {hasRulesCorrectWallet && (
              <div className="UserOperations__wallet-balance-change-btn">
                <DefaultButton textId="boUserChangeWalletBalance" onClick={handleModalToggle} />
                <BasicModal
                  isOpen={isModalOpen}
                  onRequestClose={handleModalToggle}
                  captionId="boUserChangeWalletBalance">
                  <UserWalletPaymentForm
                    isMultiWallet={addons.includes('walletAccounts')}
                    onCloseModal={handleModalToggle}
                    userId={user.id}
                  />
                </BasicModal>
              </div>
            )}

            {payments.count > 0 && (
              <div className="UserOperations__export-btn">
                <DefaultButton type="button" textId="justExportXlsx" onClick={handleCreateUserPaymentExport} />
              </div>
            )}
          </div>
        )}

        {activeTab === 'dw' && <UserPayments />}
        {activeTab === 'transfers' &&
          (internalTransfers?.accounts_enabled ||
            internalTransfers?.traders_enabled ||
            internalTransfers?.from_wallet_enabled ||
            internalTransfers?.to_wallet_enabled ||
            internalTransfers?.from_wallet_account_enabled ||
            internalTransfers?.to_wallet_account_enabled) && <UserInternalTransfers />}
        {activeTab === 'totalPayments' && <UserTotalPayments />}
        {activeTab === 'PSPs' && <UserTotalPSPs />}
      </div>
      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </section>
  );
};

UserOperations.propTypes = {
  user: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  addons: PropTypes.array.isRequired,
  createBackOfficePaymentExport: PropTypes.func.isRequired,
  theme: PropTypes.object,
  payments: PropTypes.object,
  internalTransfers: PropTypes.object.isRequired,
};

UserOperations.defaultProps = {
  payments: {},
  theme: {},
};

export default connect(
  state => ({
    profile: state.user.profile,
    user: state.backoffice.users.user,
    payments: state.backoffice.users.payments,
    internalTransfers: state.interfaceConfig.internalTransfers,
    addons: state.interfaceConfig.addons,
  }),

  {
    createBackOfficePaymentExport: ({ exportsType, pushToExportsFunc, ext, filters }) =>
      createBackOfficePaymentExport.request({ exportsType, pushToExportsFunc, ext, filters }),
  }
)(withTheme()(UserOperations));
